import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { ONBOARDING_ENDPOINTS } from '@/features/onboarding/api/endpoints';
import type {
  ConfiguracionOnboardingDto,
  OnboardingUsuarioDto,
  PaginatedList,
  PasoDetalleDto,
  PasoDto,
} from '@/features/onboarding/types/onboarding.types';

// ── User-facing queries ──

export function useOnboardingStatusQuery() {
  return useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => apiClient<OnboardingUsuarioDto>(ONBOARDING_ENDPOINTS.MI_PROGRESO),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useCompletarOnboardingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient(ONBOARDING_ENDPOINTS.COMPLETAR, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding-status'] });
    },
  });
}

export function useActualizarProgresoMutation() {
  return useMutation({
    mutationFn: (pasoActual: number) =>
      apiClient(ONBOARDING_ENDPOINTS.MI_PROGRESO, {
        method: 'POST',
        body: { pasoActual },
      }),
  });
}

export function useAceptarTerminosMutation() {
  return useMutation({
    mutationFn: (version: string = '1.0') =>
      apiClient(ONBOARDING_ENDPOINTS.ACEPTAR_TERMINOS, {
        method: 'POST',
        body: { version },
      }),
  });
}

// ── Admin: Pasos CRUD ──

export function usePasosQuery(params: { pagina?: number; tamano?: number; busqueda?: string }) {
  const searchParams = new URLSearchParams();
  if (params.pagina) searchParams.set('pagina', String(params.pagina));
  if (params.tamano) searchParams.set('tamano', String(params.tamano));
  if (params.busqueda) searchParams.set('busqueda', params.busqueda);

  return useQuery({
    queryKey: ['onboarding-pasos', params],
    queryFn: () =>
      apiClient<PaginatedList<PasoDto>>(`${ONBOARDING_ENDPOINTS.PASOS}?${searchParams.toString()}`),
  });
}

export function usePasoDetalleQuery(id: number) {
  return useQuery({
    queryKey: ['onboarding-paso', id],
    queryFn: () => apiClient<PasoDetalleDto>(ONBOARDING_ENDPOINTS.PASO(id)),
    enabled: id > 0,
  });
}

export function useCrearPasoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { titulo: string; orden: number; descripcion?: string }) =>
      apiClient<{ id: number }>(ONBOARDING_ENDPOINTS.PASOS, {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding-pasos'] });
    },
  });
}

export function useActualizarPasoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: number; titulo: string; orden: number; descripcion?: string }) =>
      apiClient(ONBOARDING_ENDPOINTS.PASO(data.id), {
        method: 'PUT',
        body: { titulo: data.titulo, orden: data.orden, descripcion: data.descripcion },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding-pasos'] });
    },
  });
}

export function useActualizarContenidoPasoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: number; contenido: Record<string, unknown>; contenidoHtml: string }) =>
      apiClient(ONBOARDING_ENDPOINTS.PASO_CONTENIDO(data.id), {
        method: 'PUT',
        body: { contenido: data.contenido, contenidoHtml: data.contenidoHtml },
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['onboarding-paso', variables.id] });
      qc.invalidateQueries({ queryKey: ['onboarding-pasos'] });
    },
  });
}

export function useEliminarPasoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient(ONBOARDING_ENDPOINTS.PASO(id), { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding-pasos'] });
    },
  });
}

// ── Admin: Configuracion ──

export function useConfiguracionOnboardingQuery() {
  return useQuery({
    queryKey: ['onboarding-configuracion'],
    queryFn: () => apiClient<ConfiguracionOnboardingDto>(ONBOARDING_ENDPOINTS.CONFIGURACION),
  });
}

export function useActualizarConfiguracionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { activo: boolean; obligatorio: boolean }) =>
      apiClient(ONBOARDING_ENDPOINTS.CONFIGURACION, {
        method: 'PUT',
        body: data,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding-configuracion'] });
    },
  });
}

export function useResetearOnboardingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient(ONBOARDING_ENDPOINTS.RESETEAR, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding-configuracion'] });
      qc.invalidateQueries({ queryKey: ['onboarding-status'] });
    },
  });
}
